import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { formatViews } from "@/utils/catalog";

interface MovieCardProps {
  movie: Movie;
  variant?: "poster" | "row" | "compact";
}

export default function MovieCard({ movie, variant = "poster" }: MovieCardProps) {
  if (variant === "row") {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:gap-5 md:p-4"
      >
        <div className="relative w-[92px] shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-[112px]">
          <div className="aspect-[2/3] w-full">
            <img
              src={movie.cover}
              alt={`${movie.title}电影封面`}
              title={`${movie.title} - 苍井空电影详情`}
              className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <span className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-accent-500 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-950">
            <i className="ri-star-fill text-[9px]" />
            {movie.rating.toFixed(1)}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h3 className="font-heading text-base text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
              {movie.title}
            </h3>
            <span className="text-xs text-foreground-400">{movie.enTitle}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500 md:text-xs">
            <span>{movie.year} 年</span>
            <span className="h-3 w-px bg-background-300" />
            <span>{movie.duration} 分钟</span>
            <span className="hidden h-3 w-px bg-background-300 sm:block" />
            <span className="hidden sm:block">导演 {movie.director}</span>
          </div>
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
            {movie.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-2.5">
            {movie.genres.map((genre) => (
              <span
                key={genre}
                className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] text-secondary-800 md:text-[11px]"
              >
                {genre}
              </span>
            ))}
            <span className="ml-auto flex items-center gap-1 text-[10px] text-foreground-400 md:text-[11px]">
              <i className="ri-eye-line text-xs" />
              {formatViews(movie.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex flex-col overflow-hidden rounded-md border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      >
        <div className="relative w-full overflow-hidden bg-background-200">
          <div className="aspect-[2/3] w-full">
            <img
              src={movie.cover}
              alt={`${movie.title}电影封面`}
              title={`${movie.title} - 苍井空电影详情`}
              className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />
          <p className="absolute inset-x-2 bottom-2 truncate font-heading text-xs text-background-50">
            {movie.title}
          </p>
        </div>
        <div className="flex items-center justify-between px-2 py-1.5 text-[10px] text-foreground-500">
          <span>{movie.year}</span>
          <span className="flex items-center gap-0.5 text-accent-700">
            <i className="ri-star-fill text-[9px]" />
            {movie.rating.toFixed(1)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/detail/${movie.id}`} className="group flex flex-col">
      <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
        <div className="aspect-[2/3] w-full">
          <img
            src={movie.cover}
            alt={`${movie.title}电影封面`}
            title={`${movie.title} - 苍井空电影详情`}
            className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/15 to-transparent" />
        <span className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded-full bg-accent-500 px-2 py-0.5 text-[11px] font-semibold text-foreground-950">
          <i className="ri-star-fill text-[9px]" />
          {movie.rating.toFixed(1)}
        </span>
        <span className="absolute right-2.5 top-2.5 rounded-full bg-foreground-950/45 px-2 py-0.5 text-[10px] text-background-100 backdrop-blur">
          {movie.year}
        </span>
        <div className="absolute inset-x-3 bottom-3">
          <h3 className="line-clamp-1 font-heading text-[15px] text-background-50 md:text-base">
            {movie.title}
          </h3>
          <p className="mt-0.5 line-clamp-1 text-[10px] text-background-300 md:text-[11px]">
            {movie.genres.join(" · ")} · {movie.duration} 分钟
          </p>
        </div>
        <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary-500/90 text-background-50 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <i className="ri-play-fill text-xl leading-none" />
        </span>
      </div>
      <p className="mt-2.5 line-clamp-2 text-[11px] leading-relaxed text-foreground-500 md:text-xs">
        {movie.summary}
      </p>
    </Link>
  );
}